import {
  CommonErrors,
  type FundingType,
  type HomeDto,
  type RotaSettingsUpdateBody
} from '@lmc/ts-common'
import {
  Add,
  Button,
  Form,
  FormCard,
  FormItem,
  PageHeader,
  Trash,
  theme
} from '@lmc/ui-kit'
import * as GlobalActions from 'actions/Global'
import { Form as AntdForm, Input } from 'antd'
import { useWatch } from 'antd/lib/form/Form'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { PageWrapper } from '../components'
import { handleSubmit } from '../formComponents/utils'
import { getIsDifferentRule, getRequiredRule } from '../utils'
import FixedFooter from './components/FixedFooter'
import SettingsPaywall from './components/SettingsPaywall'

type CarePackageSettingsForm = {
  fundingTypes: Array<FundingType>
}

const CarePackageSettingsScreen = () => {
  const { t } = useTranslation()
  const [form] = AntdForm.useForm()
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  useWatch('fundingTypes', form)

  const dispatch = useDispatch()

  const initialValues = { fundingTypes: home.rotaSettings?.fundingTypes }

  const mutationFunction = (
    fundingTypes: RotaSettingsUpdateBody['fundingTypes']
  ) =>
    postJSON({
      method: 'PUT',
      url: `/list/homes/${home.id}/rota`,
      json: {
        fundingTypes
      } as RotaSettingsUpdateBody
    })

  const { mutateAsync: updateCarePackageSettings } = useMutation<
    { data: HomeDto },
    unknown,
    RotaSettingsUpdateBody,
    void
  >(
    ['rota-settings', home.id],
    (values: RotaSettingsUpdateBody) => mutationFunction(values.fundingTypes),
    {
      onSuccess: (res) => {
        dispatch(GlobalActions.setHomeDetails(res.data))
        showToast({ title: t('ROTA_SETTINGS_UPDATED') })
      }
    }
  )

  const handleOnFinish = async (values: CarePackageSettingsForm) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      await updateCarePackageSettings(values)
      setIsUpdateLoading(false)
    } catch (err: any) {
      const errorCode = err?.code
      if (
        errorCode === CommonErrors.Codes.rotaSettingsTypeInUse ||
        errorCode === CommonErrors.Codes.contractTypeInUse
      ) {
        showToast({
          title: t(`YOU_CANNOT_DELETE_THE_ITEM_WHEN_IT_IS_IN_USE`),
          type: 'error'
        })

        const newFundingValue: CarePackageSettingsForm['fundingTypes'] =
          values.fundingTypes.map((value) => ({
            ...value,
            isDeleted: false
          }))

        form.setFieldValue('fundingTypes', newFundingValue)
      } else {
        showToast({
          title: err?.message || t('SETTINGS_ERROR'),
          type: 'error'
        })
      }
      setIsUpdateLoading(false)
    }
    setIsDirty(false)
  }

  const handleFormValuesChange = (values: CarePackageSettingsForm) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const getDeleteFieldKey = (fieldName: number) => [
    'fundingTypes',
    fieldName,
    'isDeleted'
  ]

  const deleteHandler = (fieldName: number) => {
    setIsDirty(true)
    const deleteFieldName = getDeleteFieldKey(fieldName)
    form.setFieldValue(deleteFieldName, true)
  }

  const filterDeletedFields = (fieldName: number) => {
    const deleteFieldName = getDeleteFieldKey(fieldName)
    const isDeleted = form.getFieldValue(deleteFieldName)

    return isDeleted !== true
  }

  const getIsFundingDifferentRule = (fieldName: number) => {
    const formValues = form.getFieldsValue(true)
    const currentFormValues = formValues.fundingTypes || []

    return getIsDifferentRule(currentFormValues, fieldName)
  }

  return (
    <>
      <StyledPageWrapper>
        <PageHeader
          title={t('CARE_PACKAGE')}
          subtitle={t('CARE_PACKAGES_INFO')}
          sticky
        />
        <SettingsPaywall startingText={t('CARE_PACKAGE_IS')}>
          <Form<CarePackageSettingsForm>
            form={form}
            onFinish={handleOnFinish}
            onValuesChange={handleFormValuesChange}
            initialValues={initialValues}
            validateTrigger={['']}
          >
            <FormCard title={t('FUNDING_TYPES')}>
              <AntdForm.List name="fundingTypes">
                {(fields, { add }) => {
                  const notDeletedFields = fields.filter((field) =>
                    filterDeletedFields(field.name)
                  )

                  return (
                    <FundingTypesContainer>
                      {notDeletedFields.map((field, index) => (
                        <FundingTypeContainer
                          key={['funding-type-container', index].join('-')}
                        >
                          <FormItem
                            name={[field.name, 'name']}
                            key={['fundingTypes', field.key].join('-')}
                            rules={[
                              getRequiredRule(t('FUNDING_TYPE')),
                              getIsFundingDifferentRule(field.name)
                            ]}
                          >
                            <Input />
                          </FormItem>
                          {notDeletedFields.length > 1 && (
                            <DeleteButton
                              variant="text"
                              onClick={() => deleteHandler(field.name)}
                              ariaLabel={t('DELETE_ITEM')}
                            >
                              <Trash color={theme.color.neutral.color4} />
                            </DeleteButton>
                          )}
                        </FundingTypeContainer>
                      ))}
                      <Button
                        type="button"
                        variant="text"
                        onClick={() => add()}
                      >
                        <Add />
                        {t('ADD_A_NEW_FUNDING_TYPE')}
                      </Button>
                    </FundingTypesContainer>
                  )
                }}
              </AntdForm.List>
            </FormCard>
            <FixedFooter
              isUpdateLoading={isUpdateLoading}
              isDirty={isDirty}
              handleSubmit={() => handleSubmit(form, handleOnFinish)}
            />
          </Form>
        </SettingsPaywall>
      </StyledPageWrapper>
      <NavigationModal
        canNavigate={!isDirty}
        modalContentText={t('ROTA_SETTINGS_NAVIGATION_MODAL_CONTENT')}
      />
    </>
  )
}

const StyledPageWrapper = styled(PageWrapper)`
  padding-left: 24px;
  padding-right: 24px;

  & > *:first-child {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
    margin-right: 0;
  }
`

const DeleteButton = styled(Button)`
  height: 24px;
`

const FundingTypeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const FundingTypesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 440px;
`

export default CarePackageSettingsScreen
